import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createFilter } from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import {
  FilterContext,
  Form,
  Flex,
  toast,
  Button,
  useServerResponseTranslation,
  useScreenResolution,
} from 'dash-ui';
import { RandomId } from 'dash-common';
import {
  GetAlertConfigsQuery,
  useGetAlertConfigsQuery,
} from 'gql/generated.graphql';

import { ButtonContainer, SelectContainer } from './FilterByAlerts.styles';

const HIDDEN_ITEM_ID = RandomId();

type SelectItem = {
  value: string;
  label: string;
};

const toSelectItems = (item: { _id: string; name: string }) => ({
  value: item._id,
  label: item.name,
});

const dataToItems = (
  data: GetAlertConfigsQuery | undefined,
  systemItems: Array<SelectItem>
): Array<SelectItem> => {
  const alerts = data?.getAlertConfigs.collection || [];
  return [...systemItems, ...alerts.map(toSelectItems)];
};

const FilterByAlerts = () => {
  const { t } = useTranslation(['common', 'pages']);
  const translateErrorMessage = useServerResponseTranslation();
  const { setFilterState } = useContext(FilterContext);
  const { data, refetch, loading } = useGetAlertConfigsQuery({
    fetchPolicy: 'network-only',
  });
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const options = useMemo(
    () =>
      dataToItems(data, [
        {
          value: HIDDEN_ITEM_ID,
          label: t('pages:alertPage:labels:hiddenAlert'),
        },
      ]),
    [data, t]
  );

  const handleSelectInputs = useCallback(
    (_: string, values?: string[]) => {
      setFilterState('filter', {
        alertConfigIds: values?.filter((id) => id !== HIDDEN_ITEM_ID) || [],
        isHidden: values?.includes(HIDDEN_ITEM_ID) ?? false,
      });
    },
    [setFilterState]
  );

  const loadOptionsDebounced = useDebouncedCallback(
    (inputValue: string, callback: (options: Array<SelectItem>) => void) => {
      refetch({
        ...(inputValue.length > 3 ? { searchText: inputValue } : {}),
      })
        .then(({ data }) =>
          callback(
            dataToItems(data, [
              {
                value: HIDDEN_ITEM_ID,
                label: t('pages:alertPage:labels:hiddenAlert'),
              },
            ])
          )
        )
        .catch((error) => toast.error(translateErrorMessage(error)));
    },
    500
  );

  return (
    <Form.Select
      w="100%"
      isAsync
      isMulti
      isSearchable
      isLoading={loading}
      defaultOptions={options}
      loadOptions={loadOptionsDebounced}
      placeholder={t('common:labels.filterBy')}
      onMenuOpen={refetch}
      inputPlaceholder={
        t('pages:alertPage.labels.filterByAlertPlaceholder') || ''
      }
      onChanged={handleSelectInputs}
      filterOption={(option, inputValue) => {
        if (option.value === HIDDEN_ITEM_ID) {
          return true;
        }
        return createFilter({
          matchFrom: 'any',
          stringify: (option) => `${option.label}`,
        })(option, inputValue);
      }}
      components={
        isMobile
          ? {
              SelectContainer: ({ children, ...props }) => {
                return (
                  <Flex w={'100%'}>
                    <ButtonContainer>
                      <Button square icon="setting2" variation="outline" />
                    </ButtonContainer>
                    <SelectContainer>{children}</SelectContainer>
                  </Flex>
                );
              },
            }
          : {}
      }
    />
  );
};

export default FilterByAlerts;
